import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { scrollByIdx, historyIdx, setDir, clearDir } from '../lib/viewTransition';

/**
 * Drives the cross-page SLIDE that React Router triggers (document.startViewTransition)
 * on viewTransition navigations — including native browser Back/Forward. React Router
 * emits no direction, so we publish html[data-vt-dir] ("forward" | "back", from the
 * history-index delta); index.css keys the ::view-transition-*(root) slide off it. The
 * layout effect runs INSIDE React Router's transition callback (after the old snapshot
 * is captured, before the animation), so the attribute and scroll are in place when the
 * pseudo-element styles + the new snapshot resolve.
 *
 * Scroll: we restore per-entry scroll into the NEW snapshot — forward → top, back →
 * where you left it — so it lands correctly with no jump. A continuous (throttled)
 * scroll listener records each entry's position so a Back can restore it. Renders
 * nothing; must be mounted inside the router.
 */
export function SlideDirectionBridge() {
  const location = useLocation();
  const navType = useNavigationType();
  const prevIdx = useRef<number>(historyIdx());
  const prevPath = useRef<string>(location.pathname);

  // Continuously record the current entry's scroll (throttled to one write per frame)
  // so a later Back to it restores the position.
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        scrollByIdx.set(historyIdx(), window.scrollY);
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  useLayoutEffect(() => {
    const idx = historyIdx();
    const back = idx < prevIdx.current;
    const pathChanged = location.pathname !== prevPath.current;
    prevIdx.current = idx;
    prevPath.current = location.pathname;

    setDir(back ? 'back' : 'forward');
    // No transition object is exposed by React Router, so clear the attribute after
    // the CSS slide (~300ms) has played. Self-healing: the next nav re-sets it fresh,
    // and the ::view-transition pseudos only exist while a transition is live.
    const t = window.setTimeout(clearDir, 400);

    // Bake the destination scroll into the NEW snapshot (this runs before the
    // animation). A TRAVERSE (POP — browser Back OR Forward, or an in-app
    // navigate(-1)) restores where you left that entry; a fresh PUSH to a new page
    // goes to the top. Keying on POP (not the idx-delta sign) means native Forward
    // restores too, and a PUSH that reuses a truncated forward-stack idx can't
    // wrongly restore a stale scroll.
    if (navType === 'POP') {
      const y = scrollByIdx.get(idx);
      if (y != null) window.scrollTo({ top: y, left: 0, behavior: 'instant' });
    } else if (pathChanged) {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }
    return () => window.clearTimeout(t);
    // Keyed on location.key (one run per navigation); pathname/idx are read fresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  return null;
}
